import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getTurnstileSiteKey } from "@/lib/turnstile";

const TURNSTILE_ENV_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

// Client-side throttle for repeated failed sign-in attempts on the same email
// (defence in depth — the real gate is Supabase CAPTCHA enforcement).
const LOCK_WINDOW_MS = 15 * 60 * 1000;
const MAX_ATTEMPTS = 5;
const LOCK_KEY = "vmf_auth_attempts";

type AttemptRecord = { email: string; count: number; firstAt: number; lockedUntil?: number };

function readAttempts(email: string): AttemptRecord | null {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as AttemptRecord;
    if (rec.email !== email.toLowerCase()) return null;
    if (Date.now() - rec.firstAt > LOCK_WINDOW_MS && !(rec.lockedUntil && Date.now() < rec.lockedUntil)) {
      localStorage.removeItem(LOCK_KEY);
      return null;
    }
    return rec;
  } catch {
    return null;
  }
}

function recordFailure(email: string): AttemptRecord {
  const now = Date.now();
  const existing = readAttempts(email);
  const rec: AttemptRecord = existing
    ? { ...existing, count: existing.count + 1 }
    : { email: email.toLowerCase(), count: 1, firstAt: now };
  if (rec.count >= MAX_ATTEMPTS) rec.lockedUntil = now + LOCK_WINDOW_MS;
  localStorage.setItem(LOCK_KEY, JSON.stringify(rec));
  return rec;
}

function clearFailures() {
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch {}
}

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Admin Sign In — Vending Machine Free" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [captchaStatus, setCaptchaStatus] = useState<
    "idle" | "loading" | "ready" | "expired" | "error"
  >(TURNSTILE_ENV_KEY ? "loading" : "idle");
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | undefined>(
    TURNSTILE_ENV_KEY,
  );

  useEffect(() => {
    setTurnstileSiteKey(getTurnstileSiteKey());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !TURNSTILE_ENV_KEY) return;
    const w = window as unknown as {
      __vmAuthTurnstileCb?: (t: string) => void;
      __vmAuthTurnstileExpired?: () => void;
      __vmAuthTurnstileError?: () => void;
    };
    w.__vmAuthTurnstileCb = (t: string) => {
      setCaptchaToken(t);
      setCaptchaStatus("ready");
    };
    w.__vmAuthTurnstileExpired = () => {
      setCaptchaToken("");
      setCaptchaStatus("expired");
    };
    w.__vmAuthTurnstileError = () => {
      setCaptchaToken("");
      setCaptchaStatus("error");
    };
    if (!document.querySelector('script[data-turnstile="1"]')) {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      s.dataset.turnstile = "1";
      s.onerror = () => setCaptchaStatus("error");
      document.head.appendChild(s);
    }
  }, []);

  function resetCaptcha() {
    setCaptchaToken("");
    setCaptchaStatus("loading");
    const w = window as unknown as { turnstile?: { reset: (sel?: string) => void } };
    try {
      w.turnstile?.reset(".cf-turnstile");
    } catch {}
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    // Client throttle
    const rec = readAttempts(email);
    if (rec?.lockedUntil && Date.now() < rec.lockedUntil) {
      const mins = Math.ceil((rec.lockedUntil - Date.now()) / 60000);
      return setError(
        `Too many failed attempts. Try again in ${mins} minute${mins === 1 ? "" : "s"}.`,
      );
    }

    if (TURNSTILE_ENV_KEY && !captchaToken) {
      return setError("Please complete the CAPTCHA challenge before continuing.");
    }

    setBusy(true);
    if (mode === "signup") {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
          captchaToken: captchaToken || undefined,
        },
      });
      setBusy(false);
      resetCaptcha();
      if (error) return setError(error.message);
      setMessage(
        "Account created. Share this email with the site owner so they can grant admin access.",
      );
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken: captchaToken || undefined },
    });
    setBusy(false);
    resetCaptcha();
    if (error) {
      const updated = recordFailure(email);
      const remaining = Math.max(0, MAX_ATTEMPTS - updated.count);
      if (updated.lockedUntil) {
        return setError(
          "Too many failed attempts. This device is temporarily locked out for 15 minutes.",
        );
      }
      return setError(
        `${error.message}${remaining > 0 ? ` — ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.` : ""}`,
      );
    }
    clearFailures();
    navigate({ to: "/admin/leads" });
  }

  return (
    <main className="mx-auto max-w-md px-6 py-16">
      <h1 className="text-2xl font-bold mb-2">
        {mode === "signin" ? "Admin Sign In" : "Create Admin Account"}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Restricted area — admin accounts only.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {turnstileSiteKey ? (
          <div>
            <div
              className="cf-turnstile"
              data-sitekey={turnstileSiteKey}
              data-callback="__vmAuthTurnstileCb"
              data-expired-callback="__vmAuthTurnstileExpired"
              data-error-callback="__vmAuthTurnstileError"
              data-timeout-callback="__vmAuthTurnstileExpired"
            />
            {captchaStatus === "error" && (
              <p className="text-xs text-destructive mt-1">
                CAPTCHA failed to load. Refresh the page and try again.
              </p>
            )}
            {captchaStatus === "expired" && (
              <p className="text-xs text-muted-foreground mt-1">
                CAPTCHA expired — please solve it again.
              </p>
            )}
          </div>
        ) : null}

        {error && <p className="text-sm text-destructive">{error}</p>}
        {message && <p className="text-sm text-emerald-600">{message}</p>}
        <Button
          type="submit"
          disabled={busy || (Boolean(TURNSTILE_ENV_KEY) && !captchaToken)}
          className="w-full"
        >
          {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Sign up"}
        </Button>
      </form>
      <button
        type="button"
        onClick={() => {
          setMode(mode === "signin" ? "signup" : "signin");
          setError(null);
          setMessage(null);
        }}
        className="mt-4 text-sm underline text-muted-foreground"
      >
        {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
      </button>
    </main>
  );
}
